/**
 * Parse Assessment Playbook content.
 *
 * Each playbook is a public, native sideb.io webpage rendered by /pages/Playbook.jsx
 * and matched by URL slug. Source of truth for both the React page AND the
 * backend PDF endpoint (/api/playbooks/{slug}/download.pdf) — keep these in sync
 * with backend/server.py PLAYBOOKS dict.
 */

export const PLAYBOOKS = {
  "token-efficiency": {
    slug: "token-efficiency",
    roleId: "engineering",
    title: "Token Efficiency Teardown",
    audience: "Engineering & Architecture leaders",
    audienceLong: "CTO, VP Engineering, Solutions Architect",
    intro:
      "A working blueprint to compress LLM spend, harden routing, and reclaim observability across your AI infrastructure — without locking into a single frontier vendor.",
    eyebrow: "AI infrastructure playbook",
    sections: [
      {
        title: "Token Usage Analysis",
        summary:
          "Detailed breakdown of your current token consumption patterns and where they're concentrated.",
        actions: [
          "Instrument per-team and per-feature token logging at the gateway layer — most teams discover 30–40% of spend is concentrated in 1–2 endpoints.",
          "Tag every request with model, prompt hash, and feature owner so you can attribute cost to product surfaces, not just to AWS bills.",
          "Build a weekly burn-rate dashboard with a hard ceiling per environment. Anything that breaches gets paged.",
          "Identify the 80/20: which prompts drive 80% of spend? Those are the targets for caching, compression, and routing optimization.",
        ],
      },
      {
        title: "Model Routing Optimization",
        summary:
          "Strategic recommendations for intelligent model selection across cost, latency, and capability tiers.",
        actions: [
          "Move off single-vendor default routing. A semantic router with a 3-tier model fallback (cheap / mid / frontier) typically cuts spend 35–50% with no quality regression.",
          "Define a routing matrix per use case: classification → cheap model, summarization → mid, agentic reasoning → frontier. Document and review quarterly.",
          "Add a circuit breaker per provider so a Claude or OpenAI outage doesn't take down the product. Failover should be automatic, not pager-driven.",
          "Cache embeddings and frequent completions in Redis with a TTL. Repeated context lookups (RAG fetches, system prompts) are the highest-ROI cache targets.",
        ],
      },
      {
        title: "Cost Reduction Roadmap",
        summary:
          "Prioritized actions with projected 30–40% cost savings, ranked by implementation effort.",
        actions: [
          "Quick win (week 1): enable prompt caching for system messages and stable RAG contexts. Most providers cut these tokens 50–90%.",
          "Quick win (week 2): downgrade non-critical paths to a cheaper model. Internal tooling, batch jobs, and analytics rarely need frontier.",
          "Mid-term (month 1–2): introduce structured output formats so retries from malformed JSON disappear. Saves both tokens and reliability.",
          "Long-term (quarter): standardize on a model-agnostic gateway. Your future negotiating leverage with OpenAI/Anthropic depends on it.",
        ],
      },
      {
        title: "Implementation Timeline",
        summary:
          "90-day phased rollout plan with quick wins surfaced first and structural changes staged behind them.",
        actions: [
          "Days 1–14: instrumentation + cost dashboard. You cannot optimize what you cannot measure.",
          "Days 15–45: ship prompt caching + 3-tier routing matrix for top-3 highest-spend endpoints.",
          "Days 46–75: structural — model gateway, circuit breakers, embedding cache. This is the platform investment.",
          "Days 76–90: harden, measure, and document. Hand the dashboard to finance so they stop calling you about the API bill.",
        ],
      },
    ],
    cta: {
      heading: "Want a 1-on-1 walkthrough of these recommendations against your actual stack?",
      sub: "A 15-minute call with SideB. We'll go deeper on the highest-leverage opportunities in your environment.",
    },
  },

  "ai-vendor-audit": {
    slug: "ai-vendor-audit",
    roleId: "finance",
    title: "AI Vendor Audit",
    audience: "Finance & Operations leaders",
    audienceLong: "CFO, VP Finance, Head of Operations",
    intro:
      "A vendor-agnostic framework to audit your AI tool sprawl, consolidate spend, and establish an ROI discipline before the next budget cycle.",
    eyebrow: "AI procurement playbook",
    sections: [
      {
        title: "Tool Sprawl Analysis",
        summary:
          "Complete inventory of your AI subscriptions, who's paying for them, and where capabilities overlap.",
        actions: [
          "Pull every charge tagged 'AI', 'ML', 'analytics', or 'automation' from the last 90 days of expense reports. Most companies find 2–3× more tools than IT knows about.",
          "Map each tool to a primary use case and a department owner. Anonymous tools — no owner, no business case — get a deprecation review.",
          "Identify capability overlap (e.g., 3 tools doing transcription, 4 doing copy generation). These are your first consolidation targets.",
          "Quantify shadow IT: AI features bolted onto SaaS you didn't realize you were paying for. Often 15–25% of total AI spend.",
        ],
      },
      {
        title: "Vendor Consolidation Plan",
        summary:
          "Sequencing logic to eliminate redundant tools without breaking the workflows people actually use.",
        actions: [
          "Rank candidates for elimination by: spend × low adoption × redundancy. The top 3 typically pay back the audit in month 1.",
          "Before deprecating, run a 2-week shadow period: redirect users to the consolidated tool and measure complaint volume.",
          "Re-negotiate the survivors. Annual contracts with renewal in <6 months are leverage. Bundle commitments for discount.",
          "Establish a 'one tool per category' policy with explicit escape hatches for justified exceptions. The default should be no.",
        ],
      },
      {
        title: "ROI Framework",
        summary:
          "Measurement system that ties AI investment to a real business outcome — not just feature adoption.",
        actions: [
          "Pick 1 quantifiable metric per tool: hours saved, deals influenced, support tickets deflected, content shipped. No metric → no renewal.",
          "Baseline before adoption. 'We bought it, then asked if it worked' is a budget killer. Six months of baseline > a flashy demo.",
          "Tie spend to a P&L line (CAC, COGS, opex). 'AI productivity gains' on the org chart is not a P&L line.",
          "Quarterly tool review with the owner. If the owner can't articulate the ROI in 90 seconds, the tool is on probation.",
        ],
      },
      {
        title: "Budget Optimization",
        summary:
          "Cost-benefit projections with implementation priorities sequenced for fastest payback.",
        actions: [
          "Target 25–40% AI spend reduction in 90 days. Most of it comes from consolidation, not from negotiating individual contract rates.",
          "Reinvest 30% of the savings into 1–2 high-leverage tools that solve real bottlenecks, not into more sprawl.",
          "Build a public AI tool roster (with owners + cost) on Notion or Confluence. Visibility kills shadow IT.",
          "Get IT/security on the vendor approval workflow before any new AI tool is procured. Procurement is the second-best lever; visibility is the first.",
        ],
      },
    ],
    cta: {
      heading: "Ready to put a 90-day plan against your real spend?",
      sub: "A 15-minute call with SideB. We'll walk the audit framework against your actual line items.",
    },
  },

  "signal-to-close": {
    slug: "signal-to-close",
    roleId: "sales",
    title: "Signal-To-Close Pipeline",
    audience: "Sales & Revenue leaders",
    audienceLong: "CRO, VP Sales, RevOps Leader",
    intro:
      "A playbook to embed AI into the parts of the revenue motion where it compounds — signal detection, qualification, forecast accuracy — without retraining the entire sales org.",
    eyebrow: "Revenue intelligence playbook",
    sections: [
      {
        title: "Signal Detection Strategy",
        summary:
          "A framework for identifying and acting on buying signals before your competitors do.",
        actions: [
          "Identify your 5 highest-conviction buying signals (funding round, exec hire, tech-stack change, RFP filing, peer-company expansion). Build automated alerts on each.",
          "Route signal hits into your sequencer with the specific play that fits the signal — not the generic 'we noticed you raised' email.",
          "Score signals by historical close rate. A new VP Sales hire from a peer company → high conviction. A blog post → noise.",
          "Audit your data sources weekly. Free intent data sources decay. The ones worth paying for are the ones that drive measurable pipeline.",
        ],
      },
      {
        title: "Qualification Automation",
        summary:
          "AI-powered MEDDIC extraction from call transcripts so reps stop typing and start selling.",
        actions: [
          "Pipe every sales call transcript through an LLM with a MEDDIC prompt. Push the structured output back into the CRM opportunity record.",
          "Flag opportunities missing >2 MEDDIC fields. These are the deals that ghost in week 4 — your forecast is silently overstated until you catch them.",
          "Auto-generate next-step recommendations from the transcript. Reps execute; they don't have to decide what to ask next.",
          "Train your post-call AI on your top 3 reps' deals. Generic LLM extraction is fine; tuned extraction crushes it.",
        ],
      },
      {
        title: "Pipeline Intelligence",
        summary:
          "Forecast accuracy improvements grounded in conversation analysis rather than rep sentiment.",
        actions: [
          "Replace the 'commit / best case / pipeline' classification with AI-derived close probability based on transcript sentiment + MEDDIC completeness + deal age.",
          "Audit forecast accuracy quarterly: AI-derived vs rep-derived vs actual. The deltas are your training data.",
          "Identify deal velocity outliers automatically. A deal stuck at 'demo done' for 21+ days is a ghost — surface it weekly.",
          "Build a 'deal health' Slack digest for the CRO. Top 10 deals + 3 risk flags each. Sales leadership stops chasing reps for status.",
        ],
      },
      {
        title: "Implementation Roadmap",
        summary:
          "Phased rollout sequenced for minimal rep training and fastest leadership impact.",
        actions: [
          "Phase 1 (week 1–3): pilot with 3 reps + 1 sales manager. Pick reps who actually log into the CRM.",
          "Phase 2 (week 4–8): roll out signal alerts + auto-MEDDIC across the team. Keep the UI in the existing CRM — no new tabs.",
          "Phase 3 (week 9–12): forecast-accuracy AI lives in the leadership weekly. Rep adoption is a side effect, not a goal.",
          "Maintenance: monthly review of signal sources + MEDDIC prompt drift. The play decays without ongoing tuning.",
        ],
      },
    ],
    cta: {
      heading: "Want to map this onto your actual pipeline?",
      sub: "A 15-minute call with SideB. We'll go through the signal mix and qualification gaps that matter most for your motion.",
    },
  },
  "saas-audit-framework-payments": {
    slug: "saas-audit-framework-payments",
    role: "payments",
    title: "The Payments SaaS Audit Framework",
    audience: "Payments & Compliance leaders (CFO, VP Payments, Head of Compliance, Head of Risk)",
    intro:
      "A 5-phase method for finding redundant, underused, and compliance-exposing software across payment processors and embedded-payments teams. Every duplicate fraud tool is also a potential PCI-DSS scope expansion — this playbook treats spend and risk as one problem, not two.",
    stats: [
      { label: "5-phase method", detail: "Discovery → Roadmap" },
      { label: "25–40%", detail: "typical redundant SaaS spend" },
      { label: "8 patterns", detail: "we see in every audit" },
      { label: "30-min scorecard", detail: "included as a self-assessment" },
    ],
    whatsInside: [
      "The full 5-phase audit method (Discovery, Categorization, Utilization, Risk, Rationalization) with deliverable checklists per phase.",
      "The 30-minute Quick-Reference Audit Scorecard we use on every diagnostic call — Low / Moderate / High signals across the 5 dimensions that predict a bloated stack.",
      "The 8 redundancy patterns we see in almost every payments audit (fraud/risk, KYC/KYB, sanctions/AML, gateways, disputes, reconciliation, tax/regulatory, embedded payments).",
      "The exact reconciliation sources — SSO logs, AP records, PCI AOC, DPAs — and how to cross-reference them without launching a formal procurement project.",
      "How to sequence rationalization so a tool inside the CDE moves before a cheaper tool outside — and how to quantify savings and risk reduction on the same page for executives.",
    ],
    sections: [
      {
        title: "Benchmarks — What the data shows",
        summary:
          "Directional industry averages for payments-heavy orgs. Not a substitute for a company-specific audit, but useful to size the opportunity before you start.",
        actions: [
          "Typical SaaS stack size: 200–290 applications across a payments organization — well above the non-payments average.",
          "Redundant or underutilized spend: 25–40% of total SaaS spend, concentrated in fraud/KYC/AML/reconciliation categories.",
          "Apps purchased outside IT/Compliance ('shadow IT'): up to 65% of the stack — each one a potential PCI-DSS scope expansion.",
          "Third-party vendors touching cardholder or financial data: frequently 2–3x more than leadership can name from memory.",
        ],
      },
      {
        title: "Phase 1 — Discovery & Inventory",
        summary:
          "Build the ground-truth list of every application the organization is actually using — from SSO logs, finance records, and shadow-IT surveys — cross-referenced against your PCI AOC and vendor list.",
        actions: [
          "Pull SSO/IdP logs (Okta, Azure AD, Google Workspace) to identify every app employees are actually logging into.",
          "Reconcile against finance: credit card statements, AP/vendor lists, and expense reports for tools paid outside IT.",
          "Cross-reference contracts, MSAs, DPAs, and renewal dates.",
          "Pull your PCI-DSS Attestation of Compliance (AOC) and third-party vendor list.",
          "Survey product, engineering, and risk/fraud teams for 'shadow IT' tools that never went through procurement.",
          "Deliverable: a consolidated application inventory with owner, department, cost, renewal date, and cardholder-data touchpoint flag.",
        ],
      },
      {
        title: "Phase 2 — Categorization & Mapping",
        summary:
          "Group the inventory by function, product line, and geography so overlap becomes visible — and every application's relationship to the cardholder data environment (CDE) is documented. The full phase in the PDF includes the exact function taxonomy, the CDE-mapping template we use in engagements, and the governance-gap flags that separate accidental redundancy from intentional redundancy.",
        locked: true,
        preview: [
          "Full functional taxonomy: fraud/risk, KYC/KYB, sanctions/AML, payment gateway, dispute mgmt, reconciliation, tax/regulatory, embedded-payments.",
          "The CDE stack-map template — every category, every competing tool, every CDE relationship on a single page.",
          "Governance-gap flags for tools with no clear business or compliance owner.",
        ],
      },
      {
        title: "Phase 3 — Utilization & Redundancy Analysis",
        summary:
          "Score every application for actual usage vs. contract cost, and separate intentional redundancy (backup processor) from accidental redundancy (three fraud tools nobody remembers procuring). The PDF includes the fully-loaded cost formula and the redundancy matrix we hand executives — ranking every overlapping tool by savings potential, switching cost, and necessity.",
        locked: true,
        preview: [
          "The 90-day utilization scoring rubric (heavily used / lightly used / dormant).",
          "The direct-vs-partial-vs-intentional redundancy classification.",
          "Fully-loaded cost-per-app calculation: license + transaction fees + admin time + integration maintenance.",
          "The redundancy matrix executives sign off on.",
        ],
      },
      {
        title: "Phase 4 — Risk & Compliance Exposure Assessment",
        summary:
          "Every duplicate tool is also a potential compliance exposure. The PDF walks the risk register alongside the cost register, so the roadmap in Phase 5 optimizes both at once — including the real audit findings (not theoretical ones) we've surfaced in prior engagements.",
        locked: true,
        preview: [
          "PCI-DSS AOC / SOC 2 Type II attestation checklist per data-touching app.",
          "Undocumented CDE scope-expansion flags — the finding most audits miss.",
          "Missing SSO/MFA, orphaned accounts, unrotated API keys and webhooks.",
          "Inconsistent decisioning across parallel KYC/AML vendors — a real audit finding, not a theoretical one.",
          "Overly-broad OAuth scopes granted at integration time.",
        ],
      },
      {
        title: "Phase 5 — Rationalization & Roadmap",
        summary:
          "Bucket every application into a defensible decision — Keep / Consolidate / Eliminate / Renegotiate — and sequence the moves by renewal date AND compliance dependency. The PDF includes the 90-day and 12-month roadmap templates, with owners, dependencies, and both savings + risk targets per line.",
        locked: true,
        preview: [
          "The Keep / Consolidate / Eliminate / Renegotiate decision framework.",
          "Sequencing logic — why a CDE-scope tool moves before a cheaper non-CDE tool.",
          "Executive-page format: savings and risk reduction on the same slide.",
          "The ongoing governance intake process that prevents the sprawl from quietly rebuilding.",
        ],
      },
      {
        title: "Quick-Reference Audit Scorecard",
        summary:
          "The five signals we score every payments organization on in the first 30 minutes of a diagnostic call. Use it as a self-assessment before you invest in a full audit.",
        actions: [
          "Vendors with data access vs. tracked — Low: fully reconciled · Mod: a few known gaps · High: no formal reconciliation.",
          "Fraud/KYC/AML vendors per function — Low: 1 primary · Mod: 2 (documented) · High: 3+, undocumented.",
          "% of payments-adjacent spend outside IT — Low: <15% · Mod: 15–35% · High: >35%.",
          "Offboarding process — Low: automated + audited · Mod: manual but tracked · High: ad hoc or unknown.",
          "PCI-DSS scope last reassessed — Low: <12 months · Mod: 12–24 months · High: unknown or >24 months.",
        ],
      },
      {
        title: "Common Redundancy Patterns in Payments Stacks",
        summary:
          "The eight patterns we see in almost every payments audit. If any of these describe your stack, the framework will find the money and the risk. Full pattern briefs — with the leading indicators and the typical annualized savings range — are inside the PDF.",
        locked: true,
        unlockCta: "Unlock the full playbook — download the PDF",
        preview: [
          "Fraud/Risk Scoring — standalone tools running in parallel with gateway-native filters.",
          "KYC/KYB — multiple vendors accumulated across different markets or product lines.",
          "Sanctions/AML — legacy screening vendors lingering post-acquisition.",
          "Payment Gateways — secondary processors kept 'for redundancy' that are now unmonitored.",
          "Dispute Management — dedicated platforms overlapping with processor-bundled tools.",
          "Reconciliation — spreadsheet processes running alongside paid ledger software.",
          "Tax/Regulatory — regional tools per market vs. one global compliance platform.",
          "Embedded Payments — product-led payments stacks siloed from core finance stacks.",
        ],
      },
    ],
    cta: {
      heading: "Want to see what's hiding in your payments stack?",
      sub: "A 15-minute call with SideB. We'll score your stack against the framework and tell you where the biggest cost + compliance gaps are.",
    },
  },
};

export const PLAYBOOK_SLUGS = Object.keys(PLAYBOOKS);

/** Map a Parse role id (engineering/finance/sales) → playbook slug. */
export const PLAYBOOK_SLUG_BY_ROLE = {
  engineering: "token-efficiency",
  finance: "ai-vendor-audit",
  sales: "signal-to-close",
};
